import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaTasks } from 'react-icons/fa';
import { LEGAL } from '../../config/legal';

interface LandingNavbarProps {
  onScrollTo: (id: string) => void;
}

const LandingNavbar: React.FC<LandingNavbarProps> = ({ onScrollTo }) => {
  const [scrolled, setScrolled] = useState(false);
  const navBg = useColorModeValue('whiteAlpha.900', 'blackAlpha.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const scrolledTextColor = useColorModeValue('gray.700', 'gray.200');
  const textColor = scrolled ? scrolledTextColor : 'white';
  const headingColor = scrolled ? undefined : 'white';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <Box
      as="header"
      position="sticky"
      top={0}
      zIndex={100}
      bg={scrolled ? navBg : 'transparent'}
      backdropFilter={scrolled ? 'blur(12px)' : 'none'}
      borderBottomWidth={scrolled ? '1px' : 0}
      borderColor={borderColor}
      transition="all 0.25s ease"
    >
      <Container maxW="7xl" py={4}>
        <Flex justify="space-between" align="center">
          <HStack
            as={RouterLink}
            to="/"
            spacing={3}
            _hover={{ textDecoration: 'none', opacity: 0.9 }}
          >
            <Flex
              align="center"
              justify="center"
              boxSize={10}
              borderRadius="xl"
              bgGradient="linear(to-br, blue.500, purple.500)"
              color="white"
              boxShadow="md"
            >
              <Icon as={FaTasks} boxSize={5} />
            </Flex>
            <Heading
              size="md"
              color={headingColor}
              bgGradient={scrolled ? 'linear(to-r, blue.600, purple.600)' : undefined}
              bgClip={scrolled ? 'text' : undefined}
            >
              {LEGAL.productName}
            </Heading>
          </HStack>

          <HStack spacing={{ base: 2, md: 4 }} display={{ base: 'none', sm: 'flex' }}>
            <Button
              variant="ghost"
              color={textColor}
              _hover={{ bg: scrolled ? 'gray.100' : 'whiteAlpha.200' }}
              onClick={() => onScrollTo('features')}
            >
              Features
            </Button>
            <Button
              variant="ghost"
              color={textColor}
              _hover={{ bg: scrolled ? 'gray.100' : 'whiteAlpha.200' }}
              onClick={() => onScrollTo('pricing')}
            >
              Pricing
            </Button>
            <Button
              as={RouterLink}
              to="/auth"
              variant="ghost"
              color={textColor}
              _hover={{ bg: scrolled ? 'gray.100' : 'whiteAlpha.200' }}
            >
              Sign in
            </Button>
            <Button
              as={RouterLink}
              to="/auth"
              colorScheme="blue"
              bgGradient="linear(to-r, blue.500, purple.500)"
              _hover={{ bgGradient: 'linear(to-r, blue.600, purple.600)' }}
              color="white"
            >
              Get started
            </Button>
          </HStack>

          <Button
            as={RouterLink}
            to="/auth"
            colorScheme="blue"
            size="sm"
            display={{ base: 'inline-flex', sm: 'none' }}
          >
            Get started
          </Button>
        </Flex>
      </Container>
    </Box>
  );
};

export default LandingNavbar;
